import { writable, derived } from "svelte/store";
import type { ErrorEntry } from "../interfaces/ErrorTracker";
import { errorTracker } from "lib";

const createErrorTrackerStore = () => {
  const { set, subscribe, update } = writable<ErrorEntry[]>([]);

  return {
    set,
    subscribe,
    update,
    
    // Load errors from storage
    async refresh(): Promise<void> {
      const errors = await errorTracker.getAllErrors();
      set(errors);
    },

    // Clear all errors
    async clear(): Promise<void> {
      await errorTracker.clearErrors();
      set([]);
    },

    // Retry sending stored errors
    async retrySending(): Promise<void> {
      await errorTracker.retryStoredErrors();
      this.refresh(); // Refresh to update the list
    },

    // Add a new error manually (useful for testing)
    async addError(message: string, customData?: Record<string, any>): Promise<void> {
      await errorTracker.captureError(message, 'error', customData);
      this.refresh();
    },
  };
};

export const errorTrackerStore = createErrorTrackerStore();

// Note: All errors in local storage are unsent by definition
// Once sent successfully, they're removed from local storage

export const recentErrorsStore = derived(
  errorTrackerStore,
  ($errors) => {
    const oneHourAgo = new Date(Date.now() - (60 * 60 * 1000));
    return $errors.filter(error => {
      const errorDate = new Date(error.created_at.replace(' ', 'T'));
      return errorDate > oneHourAgo;
    });
  }
);

export const errorCountByType = derived(
  errorTrackerStore,
  ($errors) => {
    const counts = { error: 0, warning: 0, info: 0 };
    $errors.forEach(error => {
      counts[error.level]++;
    });
    return counts;
  }
);